import { useEffect } from "react";
import { SellerCard } from "./components/SellerCard";
import { ArticleView } from "./components/ArticleView";
import { StucksView } from "./components/StucksView";
import { EmptyState } from "./EmptyState";
import { CategorySelector } from "./CategorySelector";
import { formatDashboardTime } from "./utils/formatters";
import { useIsMobile } from "./hooks/useIsMobile";
import { useDashboardData } from "./hooks/useDashboardData";
import { Container } from "@/shared/ui";
import styles from "./Dashboard.module.scss";
import { Card } from "@/shared/ui";
import { useSuppliersStore } from "@/shared/stores/suppliersStore";
import { useDashboardStore } from "@/shared/stores/dashboardStore";
import type { StatCategory } from "./types";

export function Dashboard() {
  const { selectedSupplierId: personId } = useSuppliersStore();

  const {
    activeCategory,
    showAll,
    toggleShowAll,
    reset: resetView,
    dashboards,
    loading,
    error,
    fetchDashboards,
  } = useDashboardStore();

  const isMobileCards = useIsMobile(401);
  const isMobileSelector = useIsMobile(480);

  useEffect(() => {
    if (personId) {
      fetchDashboards(personId);
    }
  }, [personId, fetchDashboards]);

  useEffect(() => {
    resetView();
  }, [personId, resetView]);

  const {
    cards: allCards,
    products: mappedProducts,
    stucks: mappedStucks,
  } = useDashboardData(dashboards);

  const visibleCards = showAll
    ? allCards
    : allCards.slice(0, isMobileCards ? 3 : 4);
  const latestTime = dashboards[0]?.time;

  if (!personId || loading || error || allCards.length === 0) {
    const title =
      !personId || allCards.length === 0
        ? "Данные уже загружаются"
        : loading
          ? "Загрузка..."
          : "Ошибка загрузки";

    const desc = loading
      ? "Получаем данные дэшборда"
      : !personId || allCards.length === 0
        ? "Подождите, пожалуйста"
        : (error ?? "Неизвестная ошибка");

    return <EmptyState title={title} desc={desc} />;
  }

  const categories: StatCategory[] = ["полная", "артикул", "склейки"];

  return (
    <Card variant="flush">
      <Container>
        <div
          className={`${styles.statisticsBlock} ${["артикул", "склейки"].includes(activeCategory) ? styles.mobileContainer : ""}`}
        >
          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>Статистика</h2>
              {activeCategory === "полная" && latestTime && (
                <div className={styles.relevantNote}>
                  <small>Обновлено {formatDashboardTime(latestTime)}</small>
                </div>
              )}
            </div>
            <CategorySelector
              categories={categories}
              isMobile={isMobileSelector}
            />
          </div>

          {activeCategory === "полная" && (
            <>
              <div className={styles.cardsGrid}>
                {visibleCards.map((card) => (
                  <SellerCard key={card.id} {...card} />
                ))}
              </div>
              {allCards.length > visibleCards.length && (
                <div className={styles.showMoreContainer}>
                  <button
                    className={styles.showMoreButton}
                    onClick={toggleShowAll}
                  >
                    {showAll ? "Скрыть" : "Показать еще"}
                  </button>
                </div>
              )}
            </>
          )}

          {activeCategory === "артикул" && (
            <ArticleView products={mappedProducts} />
          )}
          {activeCategory === "склейки" && <StucksView stucks={mappedStucks} />}
        </div>
      </Container>
    </Card>
  );
}
